import { BaseLayouter } from './BaseLayouter';
import { GridLayouter } from './GridLayouter';
import { CircleLayouter, CircleOutsideLayouter, CircleInsideLayouter } from './CircleLayouter';
import { SpiralLayouter } from './SpiralLayouter';
import { LineLayouter, UphillLayouter, UphillRotatedLayouter, HorizontalLayouter, VerticalLayouter } from './LineLayouter';
import { LineLayouter2 } from './LineLayouter2';
import { HexagonalLayouter, SphericalHexagonalLayouter } from './HexagonalLayouter';
import { ModernLineLayouter } from './ModernLineLayouter';
import { ModernCircleLayouter } from './ModernCircleLayouter';
import { ModernSpiralLayouter } from './ModernSpiralLayouter';
import { ModernLineAdaptiveLayouter, ModernCircleAdaptiveLayouter, ModernSpiralAdaptiveLayouter } from './ModernAdaptiveLayouters';
import { 
    ModernSphereGridLayouter, 
    ModernPlaneGridLayouter, 
    ModernSphereHexLayouter, 
    ModernSphereHexRingsLayouter, 
    ModernPlaneHexRingsLayouter, 
    ModernSphereHexHoneycombLayouter 
} from './ModernSurfaceLayouters';
import { TrackLayouter } from './TrackLayouter';
import { MapAnchorLayouter } from './MapAnchorLayouter';
import { ScatterLayouter } from './ScatterLayouter';

export type LayouterType = 
    | 'grid' 
    | 'circle' 
    | 'circle_outside' 
    | 'circle_inside'
    | 'spiral' 
    | 'line' 
    | 'line2'
    | 'uphill' 
    | 'uphill_rotated'
    | 'horizontal' 
    | 'vertical'
    | 'hexagonal'
    | 'spherical_hexagonal'
    | 'modern-line'
    | 'modern-circle'
    | 'modern-spiral'
    | 'modern-line-adaptive'
    | 'modern-circle-adaptive'
    | 'modern-spiral-adaptive'
    | 'modern-sphere-grid'
    | 'modern-sphere-hex'
    | 'modern-plane-grid'
    | 'modern-sphere-hex-rings'
    | 'modern-plane-hex-rings'
    | 'modern-sphere-hex-honeycomb'
    | 'map-anchor'
    | 'scatter';

export class LayouterFactory {
    private static layouterRegistry: Map<string, () => BaseLayouter> = new Map();
    
    // Initialize the factory with all available layouters
    static {
        LayouterFactory.registerLayouter('grid', () => new GridLayouter());
        LayouterFactory.registerLayouter('circle', () => new CircleLayouter());
        LayouterFactory.registerLayouter('circle_outside', () => new CircleOutsideLayouter());
        LayouterFactory.registerLayouter('circle_inside', () => new CircleInsideLayouter());
        LayouterFactory.registerLayouter('spiral', () => new SpiralLayouter());
        LayouterFactory.registerLayouter('line', () => new LineLayouter());
        LayouterFactory.registerLayouter('line2', () => new LineLayouter2());
        LayouterFactory.registerLayouter('uphill', () => new UphillLayouter());
        LayouterFactory.registerLayouter('uphill_rotated', () => new UphillRotatedLayouter());
        LayouterFactory.registerLayouter('horizontal', () => new HorizontalLayouter());
        LayouterFactory.registerLayouter('vertical', () => new VerticalLayouter());
        LayouterFactory.registerLayouter('hexagonal', () => new HexagonalLayouter());
        LayouterFactory.registerLayouter('spherical_hexagonal', () => new SphericalHexagonalLayouter());
        // Modern TestField Layouters
        LayouterFactory.registerLayouter('modern-line', () => new ModernLineLayouter());
        LayouterFactory.registerLayouter('modern-circle', () => new ModernCircleLayouter());
        LayouterFactory.registerLayouter('modern-spiral', () => new ModernSpiralLayouter());
        // Modern Adaptive TestField Layouters  
        LayouterFactory.registerLayouter('modern-line-adaptive', () => new ModernLineAdaptiveLayouter());
        LayouterFactory.registerLayouter('modern-circle-adaptive', () => new ModernCircleAdaptiveLayouter());
        LayouterFactory.registerLayouter('modern-spiral-adaptive', () => new ModernSpiralAdaptiveLayouter());
        // Modern Surface TestField Layouters (2D/3D)
        LayouterFactory.registerLayouter('modern-sphere-grid', () => new ModernSphereGridLayouter());
        LayouterFactory.registerLayouter('modern-plane-grid', () => new ModernPlaneGridLayouter());
        LayouterFactory.registerLayouter('modern-sphere-hex', () => new ModernSphereHexLayouter());
        LayouterFactory.registerLayouter('modern-sphere-hex-rings', () => new ModernSphereHexRingsLayouter());
        LayouterFactory.registerLayouter('modern-plane-hex-rings', () => new ModernPlaneHexRingsLayouter());
        LayouterFactory.registerLayouter('modern-sphere-hex-honeycomb', () => new ModernSphereHexHoneycombLayouter());
        // Track layouter (no-op placement; preserves imported positions)
        LayouterFactory.registerLayouter('track', () => new TrackLayouter());
        // Map anchor layouter (pins relative to a map POI)
        LayouterFactory.registerLayouter('map-anchor', () => new MapAnchorLayouter());
        // Scatter layouter (pseudo-random 3D volume placement)
        LayouterFactory.registerLayouter('scatter', () => new ScatterLayouter());
    }
    
    // Register a new layouter type
    static registerLayouter(name: string, factory: () => BaseLayouter): void {
        LayouterFactory.layouterRegistry.set(name, factory);
    }
    
    // Create a layouter by name
    static create(name: string): BaseLayouter | null {
        const factory = LayouterFactory.layouterRegistry.get(name);
        return factory ? factory() : null;
    }
    
    // Create a layouter with type safety
    static createTyped(type: LayouterType): BaseLayouter {
        const layouter = LayouterFactory.create(type);
        if (!layouter) {
            throw new Error(`Unknown layouter type: ${type}`);
        }
        return layouter;
    }
    
    // Get all available layouter names
    static getAvailableLayouters(): string[] {
        return Array.from(LayouterFactory.layouterRegistry.keys());
    }
    
    // Check if a layouter type is available
    static isAvailable(name: string): boolean {
        return LayouterFactory.layouterRegistry.has(name);
    }
    
    // Get layouter information
    static getLayouterInfo(name: string): { name: string; description: string; parameters: string[] } | null {
        const layouter = LayouterFactory.create(name);
        if (!layouter) return null;
        
        return {
            name: layouter.name,
            description: layouter.description,
            parameters: layouter.getRequiredParameters()
        };
    }
    
    // Get all layouter information
    static getAllLayouterInfo(): Array<{ name: string; description: string; parameters: string[] }> {
        return LayouterFactory.getAvailableLayouters()
            .map(name => LayouterFactory.getLayouterInfo(name))
            .filter(info => info !== null) as Array<{ name: string; description: string; parameters: string[] }>;
    }
    
    // Create layouter with validation
    static createWithValidation(name: string): BaseLayouter {
        if (!LayouterFactory.isAvailable(name)) {
            throw new Error(`Layouter '${name}' is not available. Available layouters: ${LayouterFactory.getAvailableLayouters().join(', ')}`);
        }
        
        return LayouterFactory.create(name)!;
    }
    
    // Factory methods for specific layouters
    static createGrid(): GridLayouter {
        return new GridLayouter();
    }
    
    static createCircle(): CircleLayouter {
        return new CircleLayouter();
    }
    
    static createCircleOutside(): CircleOutsideLayouter {
        return new CircleOutsideLayouter();
    }
    
    static createCircleInside(): CircleInsideLayouter {
        return new CircleInsideLayouter();
    }
    
    static createSpiral(): SpiralLayouter {
        return new SpiralLayouter();
    }
    
    static createLine(): LineLayouter {
        return new LineLayouter();
    }
    
    static createLine2(): LineLayouter2 {
        return new LineLayouter2();
    }
    
    static createUphill(): UphillLayouter {
        return new UphillLayouter();
    }
    
    static createUphillRotated(): UphillRotatedLayouter {
        return new UphillRotatedLayouter();
    }
    
    static createHorizontal(): HorizontalLayouter {
        return new HorizontalLayouter();
    }
    
    static createVertical(): VerticalLayouter {
        return new VerticalLayouter();
    }
    
    static createHexagonal(): HexagonalLayouter {
        return new HexagonalLayouter();
    }
    
    static createSphericalHexagonal(): SphericalHexagonalLayouter {
        return new SphericalHexagonalLayouter();
    }
    
    // Create layouter from configuration
    static fromConfig(config: { type: string; parameters?: any }): BaseLayouter {
        const layouter = LayouterFactory.createWithValidation(config.type);
        
        // Apply custom parameters if provided
        if (config.parameters) {
            // This would typically involve setting parameters on the layouter
            // For now, we just return the layouter as-is since parameters are handled via LayoutData
        }
        
        return layouter;
    }
    
    // Get default layouter (grid)
    static getDefault(): BaseLayouter {
        return LayouterFactory.createGrid();
    }
    
    // Validate layouter name
    static validateLayouterName(name: string): boolean {
        return LayouterFactory.isAvailable(name);
    }
    
    // Get layouter categories
    static getLayouterCategories(): { [category: string]: string[] } {
        return {
            'Grid': ['grid'],
            'Circular': ['circle', 'circle_outside', 'circle_inside'],
            'Spiral': ['spiral'],
            'Linear': ['line', 'uphill', 'uphill_rotated', 'horizontal', 'vertical'],
            'Hexagonal': ['hexagonal', 'spherical_hexagonal']
        };
    }
    
    // Get layouters by category
    static getLayoutersByCategory(category: string): string[] {
        const categories = LayouterFactory.getLayouterCategories();
        return categories[category] || [];
    }
} 